import { action, computed, observable, makeObservable, values } from 'mobx';

import { ApplicationTheme, DEFAULT_APPLICATION_THEME } from 'APP/constants/appearance';
import { storage } from 'APP/packages/storage';

import { DEFAULT_CHAT_BACKGROUND } from './Appearance.contants';
import { TChatBackground, IChatBackgroundCustomItem } from './Appearance.types';

export class Appearance {
  @observable public activeTheme: ApplicationTheme | null = storage.theme.get();
  @observable private chatBackgroundGlobal: TChatBackground = DEFAULT_CHAT_BACKGROUND;
  @observable private chatBackgroundsByEntity = observable.map<string, TChatBackground>();
  @observable private chatBackgroundsCustomMap = observable.map<
    string,
    IChatBackgroundCustomItem
  >();
  @observable private systemTheme: ApplicationTheme | null = null;

  constructor() {
    makeObservable(this);
  }

  get theme(): ApplicationTheme {
    if (!this.activeTheme) {
      return this.systemTheme || DEFAULT_APPLICATION_THEME;
    }

    return this.activeTheme;
  }

  @computed
  get isDark(): boolean {
    return this.theme === ApplicationTheme.Dark;
  }

  @computed
  get chatBackgroundsCustom(): IChatBackgroundCustomItem[] {
    return Array.from(values(this.chatBackgroundsCustomMap)).sort((a, b) => {
      return a.timestamp > b.timestamp ? -1 : 1;
    });
  }

  @action
  setTheme(theme: ApplicationTheme | null): void {
    this.activeTheme = theme;
  }

  @action
  setSystemTheme(theme: ApplicationTheme): void {
    this.systemTheme = theme;
  }

  @action
  setChatBackgroundGlobal(chatBackground: TChatBackground): void {
    this.chatBackgroundGlobal = chatBackground;
  }

  @action
  setChatBackgroundsByEntity(byEntity: Record<string, TChatBackground>): void {
    if (!byEntity) {
      return;
    }
    this.chatBackgroundsByEntity.clear();

    Object.entries(byEntity).forEach(([entityId, chatBackground]) => {
      this.setChatBackgroundForEntity(entityId, chatBackground ?? DEFAULT_CHAT_BACKGROUND);
    });
  }

  @action
  setChatBackgroundForEntity(entityId: string, chatBackground: TChatBackground): void {
    this.chatBackgroundsByEntity.set(entityId, chatBackground);
  }

  @action
  deleteChatBackgroundForEntity(entityId: string): void {
    this.chatBackgroundsByEntity.delete(entityId);
  }

  @action
  addCustomChatBackground(id: string, customBackground: IChatBackgroundCustomItem): void {
    this.chatBackgroundsCustomMap.set(id, customBackground);
  }

  @action
  setCustomChatBackgrounds(customBackgrounds: Record<string, IChatBackgroundCustomItem>): void {
    this.chatBackgroundsCustomMap.clear();

    Object.entries(customBackgrounds).forEach(([id, chatBackground]) => {
      this.addCustomChatBackground(id, chatBackground);
    });
  }

  @action
  deleteCustomChatBackground(id: string): void {
    this.chatBackgroundsCustomMap.delete(id);
  }

  getChatBackgroundForEntity(entityId?: string | null): TChatBackground {
    if (!entityId) {
      return this.chatBackgroundGlobal;
    }

    const specific = this.chatBackgroundsByEntity.get(entityId);
    return specific ?? this.chatBackgroundGlobal;
  }

  getCustomChatBackground(id: string): IChatBackgroundCustomItem | null {
    return this.chatBackgroundsCustomMap.get(id) || null;
  }
}
